import { Alert } from '@mui/material'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../..'
import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  AnswerTypes,
  DefectAssistantSuggestion,
  isQuestionUnanswered,
  OptionKinds,
  OptionValue,
  QnA,
  QuestionTypes,
} from '../../actions/defectAssistant'
import { createDefectCommentAction } from '../../actions/defectComments/actions'
import {
  applyQuestionAnswer,
  clearCreateDefectAssistantErrors,
  excludeSuggestions,
  resetAnswersFromIndex,
  rewindToRound,
  selectAssistantIsComplete,
  selectCreateDefectAssistantState,
  selectSuggestion,
  submitAdditionalDetails,
} from '../../reducers/createDefectAssistantSlice'
import { selectSuggestionRoundsVM } from '../../reducers/createDefectAssistantViewModel'
import { clearCreateStatusByDefectId } from '../../reducers/defectComments'
import ConfirmationDialog from './ConfirmationDialog'
import { AiButton, EscapeLink } from './CtaButtons'
import { FinishStep, Interlude, SuggestionsBlock } from './scroll-ai-flow/flowSections'
import {
  ActiveSingleSelect,
  FutureSection,
  LockedDuplicateCheck,
  LockedQuestion,
  PlaceholderSection,
  ScrollDuplicateCheck,
  ScrollMultiSelect,
} from './scroll-ai-flow/questionSections'
import {
  Container,
  OptionDesc,
  OptionsList,
  OptionTitle,
  SCROLL_DELAY_MS,
  ScrollOptionButton,
  Section,
  SectionQuestion,
  SectionReasoning,
} from './scroll-ai-flow/styles'
import { CreateDefectFormData } from './types'

const KNOWN_STEPS = [
  { type: QuestionTypes.DEFECT_TYPE, labelKey: 'defect.field.defectType' },
  { type: QuestionTypes.PHYSICAL_ITEM, labelKey: 'defect.aiAssistant.questionType.physicalItem' },
  { type: QuestionTypes.LOCATION_INFO, labelKey: 'defect.aiAssistant.questionType.locationInfo' },
  { type: QuestionTypes.COMPLETENESS, labelKey: 'defect.aiAssistant.questionType.completeness' },
] as const

interface ScrollAiFlowProps {
  applyAiSuggestion: (defectCode: string, defectType: string) => void
  onEnterManualNoAi: () => void
  onEnterManualAfterAi: () => void
}

const ScrollAiFlow = ({
  applyAiSuggestion,
  onEnterManualNoAi,
  onEnterManualAfterAi,
}: ScrollAiFlowProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const { setValue } = useFormContext<CreateDefectFormData>()

  const state = useTypedSelector(selectCreateDefectAssistantState)
  const { flow, body, questionAnswers } = state
  const roundVMs = useTypedSelector(selectSuggestionRoundsVM)
  const isComplete = useTypedSelector(selectAssistantIsComplete)
  const items = useTypedSelector((s) => s.defect.items.data)

  const [additionalDetails, setAdditionalDetails] = useState('')
  const [amendDefectId, setAmendDefectId] = useState<string | null>(null)
  const [showAbortConfirm, setShowAbortConfirm] = useState(false)
  const [reEditingIdx, setReEditingIdx] = useState<number | null>(null)
  const amendStatus = useTypedSelector((s) =>
    amendDefectId ? s.defectComments.createStatusByDefectId[amendDefectId] : undefined
  )

  const activeRef = useRef<HTMLDivElement>(null)
  const questionFlowThunkRef = useRef<{ abort: () => void } | null>(null)
  const pendingDuplicateCommentRef = useRef(false)

  const dispatchAdvanceQuestionFlow = () => {
    questionFlowThunkRef.current?.abort()
    questionFlowThunkRef.current = dispatch(advanceQuestionFlowAction())
  }

  const firstUnansweredIdx = questionAnswers.findIndex(isQuestionUnanswered)
  const currentQuestion = firstUnansweredIdx >= 0 ? questionAnswers[firstUnansweredIdx] : undefined
  const lastAnsweredIdx = questionAnswers.reduce(
    (acc, q, idx) => (isQuestionUnanswered(q) ? acc : idx),
    -1
  )

  const questionLoading = flow.step === 'questioning' && flow.loading
  const flowComplete = isComplete && !questionLoading && reEditingIdx === null

  useEffect(() => {
    if (reEditingIdx === null) return
    const q = questionAnswers[reEditingIdx]
    if (!q || q.answerType !== AnswerTypes.MULTISELECT) setReEditingIdx(null)
  }, [questionAnswers, reEditingIdx])

  useEffect(() => {
    if (!activeRef.current) return
    const timer = setTimeout(() => {
      activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [firstUnansweredIdx, flowComplete])

  const handlePickSuggestion = (suggestion: DefectAssistantSuggestion, roundIdx: number) => {
    applyAiSuggestion(suggestion.defectCode, '')
    dispatch(selectSuggestion({ suggestion, roundIdx }))
    dispatchAdvanceQuestionFlow()
  }

  const handleExcludeSuggestions = (roundIdx: number) => {
    questionFlowThunkRef.current?.abort()
    dispatch(excludeSuggestions(roundIdx))
  }

  const handleRewindToRound = (roundIdx: number) => {
    questionFlowThunkRef.current?.abort()
    dispatch(rewindToRound(roundIdx))
  }

  const handleExcludeOrRewind = (roundIdx: number) => {
    if (roundIdx < roundVMs.length - 1) {
      handleRewindToRound(roundIdx)
      return
    }
    handleExcludeSuggestions(roundIdx)
  }

  const handleMetaOption = (option: OptionValue, roundIdx: number) => {
    if (option.kind === OptionKinds.GIVE_UP) {
      setValue('comment', body.userComment)
      onEnterManualNoAi()
      return
    }
    handleExcludeOrRewind(roundIdx)
  }

  const handleSubmitAdditionalDetails = () => {
    const trimmed = additionalDetails.trim()
    if (trimmed.length > 0) {
      const nextComment =
        body.userComment.length > 0 ? `${body.userComment}\n\n${trimmed}` : trimmed
      setValue('comment', nextComment)
    }
    dispatch(submitAdditionalDetails(trimmed))
    setAdditionalDetails('')
    dispatch(analyzeSuggestionsAction())
  }

  const submitAnswers = (answers: OptionValue[]) => {
    if (questionLoading) return
    dispatch(applyQuestionAnswer(answers))
    dispatchAdvanceQuestionFlow()
  }

  const handleFinishAndSend = () => {
    const duplicateAnswer = state.questionAnswers.find(
      (qna) => qna.questionType === QuestionTypes.DUPLICATE_CHECK
    )?.answers?.[0]
    if (duplicateAnswer?.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT) {
      const defectId = duplicateAnswer.value != null ? String(duplicateAnswer.value) : ''
      const comment = duplicateAnswer.additionalInfo ?? ''
      if (defectId) {
        setAmendDefectId(defectId)
        dispatch(createDefectCommentAction({ defectId, comment }))
        return
      }
    }

    if (body.aiComment) setValue('aiComment', body.aiComment)
    setValue('comment', body.userComment)

    if (body.restrictionCode?.length) setValue('restrictionCode', body.restrictionCode)
    setValue('positionInVehicle', body.vehiclePosition || '')

    const resolvedItemId = body.itemId
    const matchedUniqueId =
      resolvedItemId != null
        ? Object.keys(items).find((key) => items[key].itemId === resolvedItemId)
        : undefined
    if (matchedUniqueId) {
      setValue('type', 'item')
      setValue('installationItem', matchedUniqueId)
      setValue('installationSerialNumber', items[matchedUniqueId].serialNumber)
    }
    applyAiSuggestion(body.defectCode, body.defectType)
    onEnterManualAfterAi()
  }

  // ------------------------------------------------------------------
  // Section dispatcher — picks the right component for a question's state
  // ------------------------------------------------------------------

  const renderQuestionSection = (
    qna: QnA | undefined,
    stepType: string,
    stepLabel: string,
    qnaIdxInAll: number // index in questionAnswers (-1 if not yet returned)
  ): JSX.Element | null => {
    if (reEditingIdx !== null && qnaIdxInAll > reEditingIdx) return null

    if (reEditingIdx !== null && qnaIdxInAll === reEditingIdx && qna) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect
            question={qna}
            loading={questionLoading}
            onSubmit={(answers) => {
              dispatch(resetAnswersFromIndex(reEditingIdx))
              dispatch(applyQuestionAnswer(answers))
              dispatchAdvanceQuestionFlow()
              setReEditingIdx(null)
            }}
          />
        </div>
      )
    }

    // Pure placeholder (not yet returned by backend)
    if (!qna) {
      const isFirstLoading = questionLoading && qnaIdxInAll === -1 && !currentQuestion
      return (
        <PlaceholderSection key={stepType} stepLabel={stepLabel} firstLoading={isFirstLoading} />
      )
    }

    const answered = !isQuestionUnanswered(qna)
    const isActive = currentQuestion?.uuid === qna.uuid

    if (
      qna.answerType === AnswerTypes.MULTISELECT &&
      answered &&
      questionLoading &&
      !currentQuestion &&
      qnaIdxInAll === lastAnsweredIdx
    ) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect question={qna} loading onSubmit={submitAnswers} />
        </div>
      )
    }

    if (!answered && !isActive) {
      return <FutureSection key={qna.uuid} qna={qna} stepLabel={stepLabel} />
    }

    if (answered) {
      if (qna.questionType === QuestionTypes.DUPLICATE_CHECK) {
        return (
          <LockedDuplicateCheck
            key={qna.uuid}
            qna={qna}
            onReopen={(opt) => {
              if (opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT) {
                pendingDuplicateCommentRef.current = true
              }
              dispatch(resetAnswersFromIndex(qnaIdxInAll))
            }}
            onAbort={() => setShowAbortConfirm(true)}
          />
        )
      }
      const isMulti = qna.answerType === AnswerTypes.MULTISELECT
      return (
        <LockedQuestion
          key={qna.uuid}
          qna={qna}
          onReopen={() =>
            isMulti ? setReEditingIdx(qnaIdxInAll) : dispatch(resetAnswersFromIndex(qnaIdxInAll))
          }
        />
      )
    }

    if (qna.questionType === QuestionTypes.DUPLICATE_CHECK) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollDuplicateCheck
            question={qna}
            loading={questionLoading}
            amendLoading={amendStatus === 'loading'}
            pendingCommentOpen={pendingDuplicateCommentRef}
            onSubmit={submitAnswers}
            onAmendComment={(defectId, comment) => {
              setAmendDefectId(defectId)
              dispatch(createDefectCommentAction({ defectId, comment }))
            }}
            onAbortRequest={() => setShowAbortConfirm(true)}
          />
        </div>
      )
    }
    if (qna.answerType === AnswerTypes.MULTISELECT) {
      return (
        <div key={qna.uuid} ref={activeRef}>
          <ScrollMultiSelect question={qna} loading={questionLoading} onSubmit={submitAnswers} />
        </div>
      )
    }
    return (
      <div key={qna.uuid} ref={activeRef}>
        <ActiveSingleSelect
          qna={qna}
          loading={questionLoading}
          onPick={(opt) => !questionLoading && submitAnswers([opt])}
        />
      </div>
    )
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (flow.step === 'failed') {
    return (
      <Container>
        <Alert severity="error">
          {flow.source === 'analyze'
            ? t('defect.aiAssistant.error.suggestions')
            : t('defect.aiAssistant.error.questions')}
        </Alert>
        <AiButton
          type="button"
          onClick={() => {
            dispatch(clearCreateDefectAssistantErrors())
            dispatch(analyzeSuggestionsAction())
          }}
        >
          {t('defect.aiAssistant.retry')}
        </AiButton>
      </Container>
    )
  }

  // ------------------------------------------------------------------
  // Main render — suggestions, then known steps, then finish
  // ------------------------------------------------------------------

  const knownStepTypes = KNOWN_STEPS.map((s) => s.type as string)
  const extraQuestions = questionAnswers.filter((q) => !knownStepTypes.includes(q.questionType))

  return (
    <Container>
      {roundVMs.map((round) => {
        const interlude = round.interlude ? (
          <Interlude
            mode={round.interlude.mode}
            detail={round.interlude.detail}
            value={additionalDetails}
            onChange={setAdditionalDetails}
            onSubmit={handleSubmitAdditionalDetails}
            isScrollAnchor={round.isScrollAnchor}
          />
        ) : null

        return (
          <Fragment key={round.key}>
            {round.empty ? (
              <Section>
                <SectionReasoning>{t('defect.aiAssistant.noSuggestions')}</SectionReasoning>
              </Section>
            ) : (
              <SuggestionsBlock
                round={round}
                onPick={(suggestion) => handlePickSuggestion(suggestion, round.roundIdx)}
                onMeta={(option) => handleMetaOption(option, round.roundIdx)}
                onExclude={() => handleExcludeOrRewind(round.roundIdx)}
              />
            )}
            {interlude}
            {round.showRetryEscape && (
              <Section>
                <OptionsList>
                  <ScrollOptionButton
                    type="button"
                    optionState="active"
                    onClick={onEnterManualNoAi}
                  >
                    <OptionTitle>{t('defect.aiAssistant.noneOfTheseTitle')}</OptionTitle>
                    <OptionDesc>{t('defect.aiAssistant.noneOfTheseManualReasoning')}</OptionDesc>
                  </ScrollOptionButton>
                </OptionsList>
              </Section>
            )}
          </Fragment>
        )
      })}

      {KNOWN_STEPS.map(({ type, labelKey }) => {
        return questionAnswers
          .map((qna, idx) => ({ qna, idx }))
          .filter(({ qna }) => qna.questionType === type)
          .map(({ qna, idx }) =>
            renderQuestionSection(idx >= 0 ? qna : undefined, type, labelKey, idx)
          )
      })}

      {extraQuestions.map((q) => {
        const idx = questionAnswers.indexOf(q)
        return renderQuestionSection(q, q.questionType, q.question, idx)
      })}

      {body.aiComment && flowComplete && (
        <Section>
          <SectionQuestion>{t('defect.aiAssistant.aiCommentTitle')}</SectionQuestion>
          <SectionReasoning>{body.aiComment}</SectionReasoning>
        </Section>
      )}

      <FinishStep
        flowComplete={flowComplete}
        amendLoading={amendStatus === 'loading'}
        activeRef={activeRef}
        onFinish={handleFinishAndSend}
      />

      <SectionReasoning style={{ textAlign: 'center', padding: '8px 4px' }}>
        {t('defect.aiAssistant.editSelectionsHint')}
      </SectionReasoning>

      {!flowComplete && (
        <EscapeLink type="button" onClick={onEnterManualNoAi}>
          {t('defect.aiAssistant.continueToForm')}
        </EscapeLink>
      )}

      {amendStatus === 'failed' && (
        <Alert severity="error">{t('defect.comment.create.error')}</Alert>
      )}
      {amendStatus === 'succeeded' && amendDefectId && (
        <ConfirmationDialog
          dialogText={t('defect.comment.create.success')}
          primaryActionText={t('back')}
          closeDialog={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
          }}
          submitHandler={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
            window.history.back()
          }}
        />
      )}
      {showAbortConfirm && (
        <ConfirmationDialog
          dialogText={t('defect.aiAssistant.duplicate.abortConfirmText')}
          primaryActionText={t('defect.aiAssistant.duplicate.abortConfirmButton')}
          secondaryActionText={t('defect.aiAssistant.duplicate.abortCancelButton')}
          closeDialog={() => setShowAbortConfirm(false)}
          submitHandler={() => window.history.back()}
        />
      )}
    </Container>
  )
}

export default ScrollAiFlow
